/* eslint-disable sonarjs/no-identical-functions */
import {
  EvseStatus,
  HealthGrade,
  OverviewAssetCategory,
  TimelineEntryType,
  VehicleOperationalStatus,
} from '@core/api/single-site/single-site.enums';
import {
  AssetOverviewBatteryData,
  AssetOverviewFleetData,
  AssetOverviewPocData,
  AssetOverviewResponse,
  AssetOverviewSolarData,
  AssetOverviewWebsocketMessage,
  ChargingOverviewDataUnit,
  ChargingOverviewResponse,
  ChargingOverviewWebsocketMessage,
  ChargingVehicleDetails,
  CreateTransitRouteRequest,
  DispatchGraphDataUnit,
  EvseDetails,
  EvseDetailsResponse,
  EvseDetailsWebsocketMessage,
  EvseError,
  EvseOverviewResponse,
  EvseOverviewWebsocketMessage,
  EvseTimeline,
  EvseTimelineDataUnit,
  FaultedVehicleDetails,
  GeoJson,
  GeoPosition,
  GeoPositionPair,
  GridPriceDataUnit,
  InDepotVehicleDetails,
  InfrastructureOverviewChargersData,
  InfrastructureOverviewResponse,
  InfrastructureOverviewWebsocketMessage,
  MapOverviewResponse,
  MapOverviewWebsocketMessage,
  OnTheRoadCityVehicle,
  OnTheRoadVehicleDetails,
  OnTheRoadVehiclesResponse,
  OnTheRoadVehiclesWebsocketMessage,
  PowerLimitChangedTimelineData,
  QueuedVehicleDetails,
  ReadyVehicleDetails,
  SocGraphData,
  SocGraphDataUnit,
  TransitRoute,
  TransitRoutesResponse,
  UnknownVehicleDetails,
  UpdateTransitRouteRequest,
  VehicleListResponse,
  VehicleOverviewResponse,
  VehicleOverviewWebsocketMessage,
  VehiclePluggedInTimelineData,
  VehiclesFiltersResponse,
  VehiclesFiltersWebsocketMessage,
  VehiclesWebsocketMessage,
} from '@core/api/single-site/single-site.models';

import { SortDirectionStoreModelEnum } from '@shared/enums/sort-direction.enum';
import { isDefined } from '@shared/utils/is-defined';
import { compareByDateString } from '@shared/utils/sort-utils';
import { wToKw } from '@shared/utils/w-to-kw';
import { whToKwh } from '@shared/utils/wh-to-kwh';

import {
  mapConnectorTypeApiModelEnumToStoreModelEnum,
  mapEvTypeApiModelEnumToStoreModelEnum,
  mapEvseStatusApiModelEnumToStoreModelEnum,
  mapHealthGradeApiModelEnumToStoreModelEnum,
  mapInfrastructureOverviewMicrogridControllerStatusApiModelEnumToStoreModelEnum,
  mapOcppChargePointStatusApiModelEnumToStoreModelEnum,
  mapOverviewAssetCategoryApiModelEnumToStoreModelEnum,
  mapVehicleDetailsAlertApiModelEnumToStoreModelEnum,
  mapVehicleDetailsWarningApiModelEnumToStoreModelEnum,
  mapVehicleEvseCombinedStateApiModelEnumToStoreModelEnum,
  mapVehicleOperationalStatusApiModelEnumToStoreModelEnum,
  mapWeekdayNumberToWeekday,
  mapWeekdayToNumber,
} from './single-site-enums.mappings';
import {
  EvseStatusStoreModelEnum,
  HealthGradeStoreModelEnum,
  OverviewAssetCategoryStoreModelEnum,
  TimelineEntryTypeStoreModelEnum,
  VehicleOperationalStatusStoreModelEnum,
} from './single-site.enums';
import {
  AssetOverviewBatteryDataStoreModel,
  AssetOverviewFleetDataStoreModel,
  AssetOverviewPocDataStoreModel,
  AssetOverviewResponseStoreModel,
  AssetOverviewSolarDataStoreModel,
  AssetOverviewWebsocketMessageStoreModel,
  ChargingVehicleDetailsStoreModel,
  CreateTransitRouteRequestStoreModel,
  DispatchGraphDataUnitStoreModel,
  EvseDetailsResponseStoreModel,
  EvseDetailsStoreModel,
  EvseDetailsWebsocketMessageStoreModel,
  EvseErrorStoreModel,
  EvseOverviewResponseStoreModel,
  EvseOverviewWebsocketMessageStoreModel,
  EvseSocGraphDataStoreModel,
  EvseSocGraphDataUnitStoreModel,
  EvseTimelineDataUnitStoreModel,
  EvseTimelineStoreModel,
  FaultedVehicleDetailsStoreModel,
  GeoJsonStoreModel,
  GeoPositionPairStoreModel,
  GeoPositionStoreModel,
  GridPriceDataUnitStoreModel,
  InDepotVehicleDetailsStoreModel,
  InfrastructureOverviewChargersDataStoreModel,
  InfrastructureOverviewResponseStoreModel,
  InfrastructureOverviewWebsocketMessageStoreModel,
  MapOverviewResponseStoreModel,
  MapOverviewWebsocketMessageStoreModel,
  OnTheRoadCityVehicleStoreModel,
  OnTheRoadVehicleDetailsStoreModel,
  OnTheRoadVehiclesResponseStoreModel,
  OnTheRoadVehiclesWebsocketMessageStoreModel,
  OverviewGraphDataUnitStoreModel,
  OverviewGraphResponseStoreModel,
  OverviewWebsocketMessageStoreModel,
  PowerLimitChangedTimelineDataStoreModel,
  QueuedVehicleDetailsStoreModel,
  ReadyVehicleDetailsStoreModel,
  TransitRouteStoreModel,
  TransitRoutesResponseStoreModel,
  UnknownVehicleDetailsStoreModel,
  UpdateTransitRouteRequestStoreModel,
  VehicleOverviewResponseStoreModel,
  VehicleOverviewWebsocketMessageStoreModel,
  VehiclePluggedInTimelineDataStoreModel,
  VehiclesFiltersResponseStoreModel,
  VehiclesFiltersWebsocketMessageStoreModel,
  VehiclesResponseStoreModel,
  VehiclesWebsocketMessageStoreModel,
} from './single-site.models';

export const mapOverviewGraphDataUnitApiModelToStoreModel = (dataUnit: ChargingOverviewDataUnit): OverviewGraphDataUnitStoreModel => {
  return {
    timestamp: dataUnit.timestamp,
    activePowerKw: wToKw(dataUnit.activePower),
  };
};

export const mapGridPriceDataUnitApiModelToStoreModel = (dataUnit: GridPriceDataUnit): GridPriceDataUnitStoreModel => {
  return {
    timestamp: dataUnit.timestamp,
    pricePerKwh: isDefined(dataUnit.pricePerKwh) ? dataUnit.pricePerKwh * 100 : dataUnit.pricePerKwh,
    pricePerKwhCurrency: dataUnit.pricePerKwhCurrency,
  };
};

export const mapDispatchGraphDataUnitApiModelToStoreModel = (dataUnit: DispatchGraphDataUnit): DispatchGraphDataUnitStoreModel => {
  return {
    startsAt: dataUnit.startsAt,
    endsAt: dataUnit.endsAt,
  };
};

export const mapOverviewGraphResponseApiModelToStoreModel = (response: ChargingOverviewResponse): OverviewGraphResponseStoreModel => {
  return {
    data: Object.keys(response.data).reduce(
      (acc, key) => {
        const typedKey = key as OverviewAssetCategory;
        const mappedKey = mapOverviewAssetCategoryApiModelEnumToStoreModelEnum(typedKey);
        const data = response.data[typedKey];
        if (!data) {
          return acc;
        }

        acc[mappedKey] = data.map((dataUnit) => mapOverviewGraphDataUnitApiModelToStoreModel(dataUnit));
        return acc;
      },
      {} as Record<OverviewAssetCategoryStoreModelEnum, OverviewGraphDataUnitStoreModel[]>
    ),
    gridPrice: response.gridPrice?.map((dataUnit) => mapGridPriceDataUnitApiModelToStoreModel(dataUnit)),
    dispatches: response.dispatches?.map((dataUnit) => mapDispatchGraphDataUnitApiModelToStoreModel(dataUnit)),
    physicalNotification: response.physicalNotification?.map((dataUnit) => mapOverviewGraphDataUnitApiModelToStoreModel(dataUnit)),
    now: response.now,
  };
};

export const mapOverviewWebsocketMessageApiModelToStoreModel = (
  message: ChargingOverviewWebsocketMessage
): OverviewWebsocketMessageStoreModel => {
  return mapOverviewGraphResponseApiModelToStoreModel(message);
};

export const mapInfrastructureOverviewResponseApiModelToStoreModel = (
  response: InfrastructureOverviewResponse
): InfrastructureOverviewResponseStoreModel => {
  return {
    microgridControllerStatus: mapInfrastructureOverviewMicrogridControllerStatusApiModelEnumToStoreModelEnum(
      response.microgridControllerStatus
    ),
    chargers: mapInfrastructureOverviewChargersDataApiModelToStoreModel(response.chargers),
  };
};

export const mapInfrastructureOverviewChargersDataApiModelToStoreModel = (
  data: InfrastructureOverviewChargersData
): InfrastructureOverviewChargersDataStoreModel => {
  return {
    available: data.available,
    active: data.active,
    offline: data.offline,
    faulted: data.faulted,
  };
};

export const mapInfrastructureOverviewWebsocketMessageApiModelToStoreModel = (
  message: InfrastructureOverviewWebsocketMessage
): InfrastructureOverviewWebsocketMessageStoreModel => {
  return mapInfrastructureOverviewResponseApiModelToStoreModel(message);
};

export const mapAssetOverviewSolarDataApiModelToStoreModel = (data: AssetOverviewSolarData): AssetOverviewSolarDataStoreModel => {
  return {
    activePowerKw: wToKw(data.activePower),
  };
};

export const mapAssetOverviewResponseApiModelToStoreModel = (response: AssetOverviewResponse): AssetOverviewResponseStoreModel => {
  return {
    solar: response.solar && mapAssetOverviewSolarDataApiModelToStoreModel(response.solar),
    depotLoad: response.depotLoad && mapAssetOverviewBaseloadDataApiModelToStoreModel(response.depotLoad),
    poc: mapAssetOverviewPocDataApiModelToStoreModel(response.poc),
    battery: response.battery && mapAssetOverviewBatteryDataApiModelToStoreModel(response.battery),
    fleet: mapAssetOverviewFleetDataApiModelToStoreModel(response.fleet),
  };
};

export const mapAssetOverviewBaseloadDataApiModelToStoreModel = (data: AssetOverviewSolarData): AssetOverviewSolarDataStoreModel => {
  return {
    activePowerKw: wToKw(data.activePower),
  };
};

export const mapAssetOverviewPocDataApiModelToStoreModel = (data: AssetOverviewPocData): AssetOverviewPocDataStoreModel => {
  return {
    activePowerKw: wToKw(data.activePower),
    gridLimitKw: wToKw(data.gridLimit),
    warningThreshold: wToKw(data.warningThreshold),
    alertThreshold: wToKw(data.alertThreshold),
  };
};

export const mapAssetOverviewBatteryDataApiModelToStoreModel = (data: AssetOverviewBatteryData): AssetOverviewBatteryDataStoreModel => {
  return {
    activePowerKw: wToKw(data.activePower),
    soc: data.soc,
    socInKwh: data.socInKwh,
  };
};

export const mapAssetOverviewFleetDataApiModelToStoreModel = (data: AssetOverviewFleetData): AssetOverviewFleetDataStoreModel => {
  return {
    activePowerKw: wToKw(data.activePower),
    activeEvses: data.activeEvses,
    totalEvses: data.totalEvses,
    evType: mapEvTypeApiModelEnumToStoreModelEnum(data.evType),
  };
};

export const mapAssetOverviewWebsocketMessageApiModelToStoreModel = (
  message: AssetOverviewWebsocketMessage
): AssetOverviewWebsocketMessageStoreModel => {
  const storeModel: AssetOverviewWebsocketMessageStoreModel = {};

  if (message.solar) {
    storeModel.solar = mapAssetOverviewSolarDataApiModelToStoreModel(message.solar);
  }

  if (message.depotLoad) {
    storeModel.depotLoad = mapAssetOverviewBaseloadDataApiModelToStoreModel(message.depotLoad);
  }

  if (message.poc) {
    storeModel.poc = mapAssetOverviewPocDataApiModelToStoreModel(message.poc);
  }

  if (message.battery) {
    storeModel.battery = mapAssetOverviewBatteryDataApiModelToStoreModel(message.battery);
  }

  if (message.fleet) {
    storeModel.fleet = mapAssetOverviewFleetDataApiModelToStoreModel(message.fleet);
  }

  return storeModel;
};

export const mapVehicleOverviewResponseApiModelToStoreModel = (response: VehicleOverviewResponse): VehicleOverviewResponseStoreModel => {
  return {
    statusData: Object.keys(response.statusData).reduce(
      (acc, key) => {
        const typedKey = key as VehicleOperationalStatus;
        acc[mapVehicleOperationalStatusApiModelEnumToStoreModelEnum(typedKey)] = response.statusData[typedKey];
        return acc;
      },
      {} as Record<VehicleOperationalStatusStoreModelEnum, number>
    ),
  };
};

export const mapVehicleOverviewWebsocketMessageApiModelToStoreModel = (
  message: VehicleOverviewWebsocketMessage
): VehicleOverviewWebsocketMessageStoreModel => {
  return {
    statusData: Object.keys(message.statusData).reduce(
      (acc, key) => {
        const typedKey = key as VehicleOperationalStatus;
        acc[mapVehicleOperationalStatusApiModelEnumToStoreModelEnum(typedKey)] = message.statusData[typedKey];
        return acc;
      },
      {} as Partial<Record<VehicleOperationalStatusStoreModelEnum, number>>
    ),
  };
};

export const mapEvseOverviewResponseApiModelToStoreModel = (response: EvseOverviewResponse): EvseOverviewResponseStoreModel => {
  return {
    data: Object.keys(response.data).reduce(
      (acc, key) => {
        const typedKey = key as EvseStatus;
        acc[mapEvseStatusApiModelEnumToStoreModelEnum(typedKey)] = response.data[typedKey];
        return acc;
      },
      {} as Record<EvseStatusStoreModelEnum, number>
    ),
    dominantConnectorType: response.dominantConnectorType && mapConnectorTypeApiModelEnumToStoreModelEnum(response.dominantConnectorType),
  };
};

export const mapEvseOverviewWebsocketMessageApiModelToStoreModel = (
  message: EvseOverviewWebsocketMessage
): EvseOverviewWebsocketMessageStoreModel => {
  return {
    data: Object.keys(message.data).reduce(
      (acc, key) => {
        const typedKey = key as EvseStatus;
        acc[mapEvseStatusApiModelEnumToStoreModelEnum(typedKey)] = message.data[typedKey];
        return acc;
      },
      {} as Partial<Record<EvseStatusStoreModelEnum, number>>
    ),
  };
};

export const mapMapOverviewResponseApiModelToStoreModel = (response: MapOverviewResponse): MapOverviewResponseStoreModel => {
  return {
    evses: response.evses.map((evseDetails) => mapEvseDetailsApiModelToStoreModel(evseDetails)),
    besses: response.besses,
    siteBoundary: mapGeoJsonApiModelToStoreModel(response.siteBoundary),
  };
};

export const mapEvseDetailsApiModelToStoreModel = (evse: EvseDetails): EvseDetailsStoreModel => {
  return {
    id: evse.id,
    position: mapGeoPositionApiModelToStoreModel(evse.position),
    connectorType: mapConnectorTypeApiModelEnumToStoreModelEnum(evse.connectorType),
    combinedStatus: mapVehicleEvseCombinedStateApiModelEnumToStoreModelEnum(evse.status),
    soc: evse.soc,
    powerKw: isDefined(evse.activePower) ? wToKw(evse.activePower) : undefined,
    powerOverride: evse.powerOverride,
  };
};

export const mapGeoPositionApiModelToStoreModel = (position: GeoPosition): GeoPositionStoreModel => {
  return {
    longitude: position.longitude,
    latitude: position.latitude,
  };
};

export const mapGeoJsonApiModelToStoreModel = (geoJson: GeoJson): GeoJsonStoreModel => {
  return {
    type: geoJson.type,
    coordinates: geoJson.coordinates.map((coordinatesArrayArray) => {
      return coordinatesArrayArray.map((coordinates) => {
        return mapGeoPositionPairApiModelToStoreModel(coordinates);
      });
    }),
  };
};

export const mapGeoPositionPairApiModelToStoreModel = (position: GeoPositionPair): GeoPositionPairStoreModel => {
  return [...position];
};

export const mapMapOverviewWebsocketMessageApiModelToStoreModel = (
  message: MapOverviewWebsocketMessage
): MapOverviewWebsocketMessageStoreModel => {
  return {
    evses: message.evses.map((evseDetails) => mapEvseDetailsApiModelToStoreModel(evseDetails)),
  };
};

export const mapOnTheRoadCityVehicleApiModelToStoreModel = (vehicle: OnTheRoadCityVehicle): OnTheRoadCityVehicleStoreModel => {
  return {
    vehicleId: vehicle.vehicleId,
    vehicleName: vehicle.vehicleName ?? undefined,
    soc: vehicle.soc,
    gpsCoordinate: vehicle.gpsCoordinate ? mapGeoPositionApiModelToStoreModel(vehicle.gpsCoordinate) : undefined,
    socHealthGrade: vehicle.socHealthGrade ? mapHealthGradeApiModelEnumToStoreModelEnum(vehicle.socHealthGrade) : undefined,
    efficiency: vehicle.efficiency,
    efficiencyHealthGrade: vehicle.efficiencyHealthGrade
      ? mapHealthGradeApiModelEnumToStoreModelEnum(vehicle.efficiencyHealthGrade)
      : undefined,
  };
};

export const mapOnTheRoadVehiclesResponseApiModelToStoreModel = (
  response: OnTheRoadVehiclesResponse
): OnTheRoadVehiclesResponseStoreModel => {
  return {
    vehicles: response.vehicles.map((vehicle) => mapOnTheRoadCityVehicleApiModelToStoreModel(vehicle)),
    depotCoordinate: response.depotCoordinate ? mapGeoPositionApiModelToStoreModel(response.depotCoordinate) : undefined,
  };
};

export const mapOnTheRoadVehiclesWebsocketMessageApiModelToStoreModel = (
  message: OnTheRoadVehiclesWebsocketMessage
): OnTheRoadVehiclesWebsocketMessageStoreModel => {
  return {
    vehicles: message.vehicles.map((vehicle) => mapOnTheRoadCityVehicleApiModelToStoreModel(vehicle)),
  };
};

export const mapEvseDetailsResponseApiModelToStoreModel = (evseDetailsResponse: EvseDetailsResponse): EvseDetailsResponseStoreModel => {
  return {
    evseId: evseDetailsResponse.evseId,
    evseName: evseDetailsResponse.evseName,
    connectorType: mapConnectorTypeApiModelEnumToStoreModelEnum(evseDetailsResponse.connectorType),
    status: mapVehicleEvseCombinedStateApiModelEnumToStoreModelEnum(evseDetailsResponse.status),
    chargerId: evseDetailsResponse.chargerId,
    chargerName: evseDetailsResponse.chargerName,
    powerSplitSiblingChargers: evseDetailsResponse.powerSplitSiblingChargers,
    error: evseDetailsResponse.error && mapEvseErrorApiModelToStoreModel(evseDetailsResponse.error),
    vehicleUUID: evseDetailsResponse.vehicleUUID,
    vehicleName: evseDetailsResponse.vehicleName,
    soc: evseDetailsResponse.soc,
    socGraphData: evseDetailsResponse.socGraphData && mapEvseSocGraphDataApiModelToStoreModel(evseDetailsResponse.socGraphData),
    departureTime: evseDetailsResponse.departureTime,
    requiredEnergyKwh: evseDetailsResponse.requiredEnergy && whToKwh(evseDetailsResponse.requiredEnergy),
    cumulativeEnergyKwh: evseDetailsResponse.cumulativeEnergy && whToKwh(evseDetailsResponse.cumulativeEnergy),
    activePowerKw: evseDetailsResponse.activePower && wToKw(evseDetailsResponse.activePower),
    maxPowerKw: wToKw(evseDetailsResponse.maxPower),
    evsePowerLimitKw: evseDetailsResponse.evsePowerLimit && wToKw(evseDetailsResponse.evsePowerLimit),
    hasActiveSession: evseDetailsResponse.hasActiveSession,
    chargerHasActiveSession: evseDetailsResponse.chargerHasActiveSession,
    powerOverride: evseDetailsResponse.powerOverride,
    manuallyPrioritized: evseDetailsResponse.manuallyPrioritized,
    rawOcppEvseStatus:
      evseDetailsResponse.rawOcppEvseStatus && mapOcppChargePointStatusApiModelEnumToStoreModelEnum(evseDetailsResponse.rawOcppEvseStatus),
    sessionStart: evseDetailsResponse.sessionStart,
    timeline: evseDetailsResponse.timeline && mapEvseTimelineApiModelToStoreModel(evseDetailsResponse.timeline),
  };
};

export const mapEvseDetailsWebsocketMessageApiModelToStoreModel = (
  message: EvseDetailsWebsocketMessage
): EvseDetailsWebsocketMessageStoreModel => {
  return {
    status: mapVehicleEvseCombinedStateApiModelEnumToStoreModelEnum(message.status),
    error: message.error && mapEvseErrorApiModelToStoreModel(message.error),

    vehicleUUID: message.vehicleUUID,
    vehicleName: message.vehicleName,
    soc: message.soc,
    socGraphData: message.socGraphData && mapEvseSocGraphDataApiModelToStoreModel(message.socGraphData),
    departureTime: message.departureTime,
    requiredEnergyKwh: message.requiredEnergy && whToKwh(message.requiredEnergy),
    activePowerKw: message.activePower && wToKw(message.activePower),
    evsePowerLimitKw: message.evsePowerLimit && wToKw(message.evsePowerLimit),
    rawOcppEvseStatus: message.rawOcppEvseStatus && mapOcppChargePointStatusApiModelEnumToStoreModelEnum(message.rawOcppEvseStatus),
    hasActiveSession: message.hasActiveSession,
    chargerHasActiveSession: message.chargerHasActiveSession,
    powerOverride: message.powerOverride,
    manuallyPrioritized: message.manuallyPrioritized,
    sessionStart: message.sessionStart,
    timeline: message.timeline && mapEvseTimelineApiModelToStoreModel(message.timeline),
  };
};

export const mapEvseErrorApiModelToStoreModel = (error: EvseError): EvseErrorStoreModel => {
  return {
    title: error.title,
    instruction: error.instruction,
    content: error.content,
  };
};

export const mapEvseSocGraphDataApiModelToStoreModel = (socGraphData: SocGraphData): EvseSocGraphDataStoreModel => {
  let data = socGraphData.data
    ?.map((dataUnit) => mapEvseSocGraphDataUnitApiModelToStoreModel(dataUnit))
    .sort((a, b) => compareByDateString(a.timestamp, b.timestamp, SortDirectionStoreModelEnum.Asc));

  if (!data?.length) {
    data = undefined;
  }

  return {
    bandThresholds: Object.entries(socGraphData.bandThresholds).reduce(
      (acc, [key, bandThreshold]) => {
        const typedKey = key as HealthGrade;
        acc[mapHealthGradeApiModelEnumToStoreModelEnum(typedKey)] = bandThreshold;
        return acc;
      },
      {} as Record<HealthGradeStoreModelEnum, number>
    ),
    data,
  };
};

export const mapEvseSocGraphDataUnitApiModelToStoreModel = (dataUnit: SocGraphDataUnit): EvseSocGraphDataUnitStoreModel => {
  return {
    timestamp: dataUnit.timestamp,
    soc: dataUnit.soc,
    charging: dataUnit.charging,
  };
};

export const mapEvseTimelineApiModelToStoreModel = (timeline: EvseTimeline): EvseTimelineStoreModel => {
  return {
    data: timeline.data.map((dataUnit) => mapEvseTimelineDataUnitApiModelToStoreModel(dataUnit)),
  };
};

export const mapEvseTimelineDataUnitApiModelToStoreModel = (dataUnit: EvseTimelineDataUnit): EvseTimelineDataUnitStoreModel => {
  return {
    timestamp: dataUnit.timestamp,
    alert: dataUnit.alert,
    data: mapEvseTimelineDataApiModelToStoreModel(dataUnit.data),
  };
};

export const mapEvseTimelineDataApiModelToStoreModel = (
  data: PowerLimitChangedTimelineData | VehiclePluggedInTimelineData
): PowerLimitChangedTimelineDataStoreModel | VehiclePluggedInTimelineDataStoreModel => {
  switch (data.type) {
    case TimelineEntryType.PowerLimitChanged: {
      return {
        type: TimelineEntryTypeStoreModelEnum.PowerLimitChanged,
        increased: data.increased,
        powerLimitKw: wToKw(data.powerLimit),
      };
    }
    case TimelineEntryType.VehiclePluggedIn: {
      return {
        type: TimelineEntryTypeStoreModelEnum.VehiclePluggedIn,
      };
    }
  }
};

export const mapReadyVehicleDetailsApiModelToStoreModel = (vehicleDetails: ReadyVehicleDetails): ReadyVehicleDetailsStoreModel => {
  return {
    vehicleUUID: vehicleDetails.vehicleUUID,
    siteId: vehicleDetails.siteId,
    vehicleName: vehicleDetails.vehicleDisplayName,
    soc: vehicleDetails.soc,
    estimatedRangeForCopySnapshot: vehicleDetails.estimatedRangeForCopySnapshot ?? undefined,
    zone: vehicleDetails.zone === 'MockZone' ? undefined : vehicleDetails.zone, // TODO BE hardcodes 'MockZone' for now.
    evseName: vehicleDetails.evseName,
    chargerName: vehicleDetails.chargerName,
    energyDeliveredKwh:
      vehicleDetails.energyDelivered === 0 ? undefined : vehicleDetails.energyDelivered && whToKwh(vehicleDetails.energyDelivered),
  };
};

export const mapChargingVehicleDetailsApiModelToStoreModel = (vehicleDetails: ChargingVehicleDetails): ChargingVehicleDetailsStoreModel => {
  return {
    vehicleUUID: vehicleDetails.vehicleUUID,
    siteId: vehicleDetails.siteId,
    vehicleName: vehicleDetails.vehicleDisplayName,
    soc: vehicleDetails.soc,
    estimatedRangeForCopySnapshot: vehicleDetails.estimatedRangeForCopySnapshot ?? undefined,
    zone: vehicleDetails.zone === 'MockZone' ? undefined : vehicleDetails.zone, // TODO BE hardcodes 'MockZone' for now.
    evseName: vehicleDetails.evseName,
    chargerName: vehicleDetails.chargerName,
    powerKw: vehicleDetails.activePower && -1 * wToKw(vehicleDetails.activePower), // TODO BE sends as negative for now
    // TODO BE hardcodes 0 for now
    targetPowerKw: vehicleDetails.targetPower === 0 ? undefined : vehicleDetails.targetPower && wToKw(vehicleDetails.targetPower),
    // TODO BE hardcodes 0 for now
    energyDeliveredKwh:
      vehicleDetails.energyDelivered === 0 ? undefined : vehicleDetails.energyDelivered && whToKwh(vehicleDetails.energyDelivered),
    warning: vehicleDetails.warning && mapVehicleDetailsWarningApiModelEnumToStoreModelEnum(vehicleDetails.warning),
    alert: vehicleDetails.alert && mapVehicleDetailsAlertApiModelEnumToStoreModelEnum(vehicleDetails.alert),
  };
};

export const mapQueuedVehicleDetailsApiModelToStoreModel = (vehicleDetails: QueuedVehicleDetails): QueuedVehicleDetailsStoreModel => {
  return {
    vehicleUUID: vehicleDetails.vehicleUUID,
    siteId: vehicleDetails.siteId,
    vehicleName: vehicleDetails.vehicleDisplayName,
    soc: vehicleDetails.soc,
    estimatedRangeForCopySnapshot: vehicleDetails.estimatedRangeForCopySnapshot ?? undefined,
    zone: vehicleDetails.zone === 'MockZone' ? undefined : vehicleDetails.zone, // TODO BE hardcodes 'MockZone' for now.
    evseName: vehicleDetails.evseName,
    chargerName: vehicleDetails.chargerName,
    powerKw: vehicleDetails.activePower && -1 * wToKw(vehicleDetails.activePower), // TODO BE sends as negative for now
    // TODO BE hardcodes 0 for now
    targetPowerKw: vehicleDetails.targetPower === 0 ? undefined : vehicleDetails.targetPower && wToKw(vehicleDetails.targetPower),
    // TODO BE hardcodes 0 for now
    energyDeliveredKwh:
      vehicleDetails.energyDelivered === 0 ? undefined : vehicleDetails.energyDelivered && whToKwh(vehicleDetails.energyDelivered),
    warning: vehicleDetails.warning && mapVehicleDetailsWarningApiModelEnumToStoreModelEnum(vehicleDetails.warning),
    alert: vehicleDetails.alert && mapVehicleDetailsAlertApiModelEnumToStoreModelEnum(vehicleDetails.alert),
  };
};

export const mapFaultedVehicleDetailsApiModelToStoreModel = (vehicleDetails: FaultedVehicleDetails): FaultedVehicleDetailsStoreModel => {
  return {
    vehicleUUID: vehicleDetails.vehicleUUID,
    siteId: vehicleDetails.siteId,
    vehicleName: vehicleDetails.vehicleDisplayName,
    soc: vehicleDetails.soc,
    estimatedRangeForCopySnapshot: vehicleDetails.estimatedRangeForCopySnapshot ?? undefined,
    zone: vehicleDetails.zone === 'MockZone' ? undefined : vehicleDetails.zone, // TODO BE hardcodes 'MockZone' for now.
    evseName: vehicleDetails.evseName,
    chargerName: vehicleDetails.chargerName,
    // TODO BE hardcodes 0 for now
    energyDeliveredKwh:
      vehicleDetails.energyDelivered === 0 ? undefined : vehicleDetails.energyDelivered && whToKwh(vehicleDetails.energyDelivered),
    warning: vehicleDetails.warning && mapVehicleDetailsWarningApiModelEnumToStoreModelEnum(vehicleDetails.warning),
    alert: vehicleDetails.alert && mapVehicleDetailsAlertApiModelEnumToStoreModelEnum(vehicleDetails.alert),
  };
};

export const mapInDepotVehicleDetailsApiModelToStoreModel = (vehicleDetails: InDepotVehicleDetails): InDepotVehicleDetailsStoreModel => {
  return {
    vehicleUUID: vehicleDetails.vehicleUUID,
    siteId: vehicleDetails.siteId,
    vehicleName: vehicleDetails.vehicleDisplayName,
    soc: vehicleDetails.soc,
    estimatedRangeForCopySnapshot: vehicleDetails.estimatedRangeForCopySnapshot ?? undefined,
    socHealthGrade: vehicleDetails.socHealthGrade && mapHealthGradeApiModelEnumToStoreModelEnum(vehicleDetails.socHealthGrade),
    arrivalTime: vehicleDetails.arrivalTime ?? undefined,
    arrivalTimeHealthGrade:
      (vehicleDetails.arrivalTimeHealthGrade && mapHealthGradeApiModelEnumToStoreModelEnum(vehicleDetails.arrivalTimeHealthGrade)) ??
      undefined,
    expectedPluginTime: vehicleDetails.expectedPluginTime ?? undefined,
    expectedPluginTimeHealthGrade:
      (vehicleDetails.expectedPluginTimeHealthGrade &&
        mapHealthGradeApiModelEnumToStoreModelEnum(vehicleDetails.expectedPluginTimeHealthGrade)) ??
      undefined,
    expectedDepartureTime: vehicleDetails.expectedDepartureTime ?? undefined,
    expectedSoc: vehicleDetails.expectedSoc ?? undefined,
    energyNeededKwh: whToKwh(vehicleDetails.energyNeeded),
    warning: vehicleDetails.warning && mapVehicleDetailsWarningApiModelEnumToStoreModelEnum(vehicleDetails.warning),
    alert: vehicleDetails.alert && mapVehicleDetailsAlertApiModelEnumToStoreModelEnum(vehicleDetails.alert),
  };
};

export const mapUnknownVehicleDetailsApiModelToStoreModel = (vehicleDetails: UnknownVehicleDetails): UnknownVehicleDetailsStoreModel => {
  return {
    vehicleUUID: vehicleDetails.vehicleUUID,
    siteId: vehicleDetails.siteId,
    vehicleName: vehicleDetails.vehicleDisplayName,
    lastKnownState: vehicleDetails.lastKnownState && mapVehicleOperationalStatusApiModelEnumToStoreModelEnum(vehicleDetails.lastKnownState),
    lastKnownTimestamp: vehicleDetails.lastKnownTimestamp,
    lastKnownSoc: vehicleDetails.lastKnownSoc,
    estimatedRangeForCopySnapshot: vehicleDetails.estimatedRangeForCopySnapshot ?? undefined,
  };
};

export const mapOnTheRoadVehicleDetailsApiModelToStoreModel = (
  vehicleDetails: OnTheRoadVehicleDetails
): OnTheRoadVehicleDetailsStoreModel => {
  return {
    vehicleUUID: vehicleDetails.vehicleUUID,
    siteId: vehicleDetails.siteId,
    vehicleName: vehicleDetails.vehicleDisplayName,
    soc: vehicleDetails.soc,
    socHealthGrade: vehicleDetails.socHealthGrade && mapHealthGradeApiModelEnumToStoreModelEnum(vehicleDetails.socHealthGrade),
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    efficiencyKwhPerUnit: isDefined(vehicleDetails.efficiency) ? whToKwh(vehicleDetails.efficiency, 2) : undefined,
    efficiencyHealthGrade:
      vehicleDetails.efficiencyHealthGrade && mapHealthGradeApiModelEnumToStoreModelEnum(vehicleDetails.efficiencyHealthGrade),
    estimatedRangeUnit: vehicleDetails.estimatedRange ?? undefined,
    estimatedRangeForCopySnapshot: vehicleDetails.estimatedRangeForCopySnapshot ?? undefined,
    timeOnRoadMsec: vehicleDetails.timeOnRoad,
    energyUsedKwh: vehicleDetails.energyUsed && whToKwh(vehicleDetails.energyUsed),
    distanceTraveledUnit: vehicleDetails.distanceTraveled,
    expectedArrivalTime: vehicleDetails.expectedArrivalTime ?? undefined,
    warning: vehicleDetails.warning && mapVehicleDetailsWarningApiModelEnumToStoreModelEnum(vehicleDetails.warning),
    alert: vehicleDetails.alert && mapVehicleDetailsAlertApiModelEnumToStoreModelEnum(vehicleDetails.alert),
  };
};

export const mapVehiclesResponseApiModelToStoreModel = (response: VehicleListResponse): VehiclesResponseStoreModel => {
  return {
    vehicles: {
      [VehicleOperationalStatusStoreModelEnum.Ready]: response.vehicles.ready.map((vehicleDetails) =>
        mapReadyVehicleDetailsApiModelToStoreModel(vehicleDetails)
      ),
      [VehicleOperationalStatusStoreModelEnum.Charging]: response.vehicles.charging.map((vehicleDetails) =>
        mapChargingVehicleDetailsApiModelToStoreModel(vehicleDetails)
      ),
      [VehicleOperationalStatusStoreModelEnum.Queued]: response.vehicles.queued.map((vehicleDetails) =>
        mapQueuedVehicleDetailsApiModelToStoreModel(vehicleDetails)
      ),
      [VehicleOperationalStatusStoreModelEnum.Faulted]: response.vehicles.faulted.map((vehicleDetails) =>
        mapFaultedVehicleDetailsApiModelToStoreModel(vehicleDetails)
      ),
      [VehicleOperationalStatusStoreModelEnum.InDepot]: response.vehicles.inDepot.map((vehicleDetails) =>
        mapInDepotVehicleDetailsApiModelToStoreModel(vehicleDetails)
      ),
      [VehicleOperationalStatusStoreModelEnum.OnTheRoad]: response.vehicles.onTheRoad.map((vehicleDetails) =>
        mapOnTheRoadVehicleDetailsApiModelToStoreModel(vehicleDetails)
      ),
      [VehicleOperationalStatusStoreModelEnum.Unknown]: response.vehicles.unknown.map((vehicleDetails) =>
        mapUnknownVehicleDetailsApiModelToStoreModel(vehicleDetails)
      ),
    },
    count: response.count,
  };
};

export const mapVehiclesWebsocketMessageApiModelToStoreModel = (message: VehiclesWebsocketMessage): VehiclesWebsocketMessageStoreModel => {
  return {
    vehicles: {
      [VehicleOperationalStatusStoreModelEnum.Ready]:
        message.vehicles.ready &&
        message.vehicles.ready.map((vehicleDetails) => mapReadyVehicleDetailsApiModelToStoreModel(vehicleDetails)),
      [VehicleOperationalStatusStoreModelEnum.Charging]:
        message.vehicles.charging &&
        message.vehicles.charging.map((vehicleDetails) => mapChargingVehicleDetailsApiModelToStoreModel(vehicleDetails)),
      [VehicleOperationalStatusStoreModelEnum.Queued]:
        message.vehicles.queued &&
        message.vehicles.queued.map((vehicleDetails) => mapQueuedVehicleDetailsApiModelToStoreModel(vehicleDetails)),
      [VehicleOperationalStatusStoreModelEnum.Faulted]:
        message.vehicles.faulted &&
        message.vehicles.faulted.map((vehicleDetails) => mapFaultedVehicleDetailsApiModelToStoreModel(vehicleDetails)),
      [VehicleOperationalStatusStoreModelEnum.InDepot]:
        message.vehicles.inDepot &&
        message.vehicles.inDepot.map((vehicleDetails) => mapInDepotVehicleDetailsApiModelToStoreModel(vehicleDetails)),
      [VehicleOperationalStatusStoreModelEnum.OnTheRoad]:
        message.vehicles.onTheRoad &&
        message.vehicles.onTheRoad.map((vehicleDetails) => mapOnTheRoadVehicleDetailsApiModelToStoreModel(vehicleDetails)),
      [VehicleOperationalStatusStoreModelEnum.Unknown]:
        message.vehicles.unknown &&
        message.vehicles.unknown.map((vehicleDetails) => mapUnknownVehicleDetailsApiModelToStoreModel(vehicleDetails)),
    },
    count: message.count,
  };
};

export const mapVehiclesFiltersResponseApiModelToStoreModel = (response: VehiclesFiltersResponse): VehiclesFiltersResponseStoreModel => {
  return {
    statuses: response.statuses.map((status) => mapVehicleOperationalStatusApiModelEnumToStoreModelEnum(status)),
    zones: response.zones,
    issues: response.issues.map((issue) => mapVehicleDetailsAlertApiModelEnumToStoreModelEnum(issue)),
  };
};

export const mapVehiclesFiltersWebsocketMessageApiModelToStoreModel = (
  message: VehiclesFiltersWebsocketMessage
): VehiclesFiltersWebsocketMessageStoreModel => {
  return {
    statuses: message.statuses.map((status) => mapVehicleOperationalStatusApiModelEnumToStoreModelEnum(status)),
    zones: message.zones,
    issues: message.issues.map((issue) => mapVehicleDetailsAlertApiModelEnumToStoreModelEnum(issue)),
  };
};

export const mapTransitRoutesResponseApiModelToStoreModel = (
  transitRoutesResponse: TransitRoutesResponse
): TransitRoutesResponseStoreModel => {
  return {
    siteId: transitRoutesResponse.siteId,
    transitRoutes: transitRoutesResponse.transitRoutes.map((transitRoute) => mapTransitRouteApiModelToStoreModel(transitRoute)),
  };
};

export const mapTransitRouteApiModelToStoreModel = (transitRoute: TransitRoute): TransitRouteStoreModel => {
  return {
    id: transitRoute.id,
    name: transitRoute.name,
    validityStart: transitRoute.validityStart,
    validityEnd: transitRoute.validityEnd,
    operatingWeekDays: transitRoute.operatingWeekDays.map((weekday) => mapWeekdayToNumber(weekday)),
    vehicleId: transitRoute.vehicleId,
    departureTime: transitRoute.departureTime,
    arrivalTime: transitRoute.arrivalTime,
    departureSoc: transitRoute.departureSoc,
    arrivalSoc: transitRoute.arrivalSoc,
    routeLength: transitRoute.routeLength,
  };
};

export const mapCreateTransitRouteRequestStoreModelToApiModel = (
  createTransitRouteRequest: CreateTransitRouteRequestStoreModel | UpdateTransitRouteRequestStoreModel
): CreateTransitRouteRequest | UpdateTransitRouteRequest => ({
  name: createTransitRouteRequest.name,
  validityStart: createTransitRouteRequest.validityStart,
  validityEnd: createTransitRouteRequest.validityEnd,
  operatingWeekDays: createTransitRouteRequest.operatingWeekDays.map((weekdayNumber) => mapWeekdayNumberToWeekday(weekdayNumber)),
  vehicleId: createTransitRouteRequest.vehicleId,
  departureTime: createTransitRouteRequest.departureTime,
  arrivalTime: createTransitRouteRequest.arrivalTime,
  departureSoc: createTransitRouteRequest.departureSoc,
  arrivalSoc: createTransitRouteRequest.arrivalSoc,
  routeLength: createTransitRouteRequest.routeLength,
});
